import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Chip,
    Paper,
    IconButton,
    CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { LpsShipment, DeviceType } from '../lps.types';
import { getShipment } from '../lps.api';

interface Props {
    open: boolean;
    shipmentUuid: string | null;
    onClose: () => void;
}

const TYPE_LABELS: Record<DeviceType, string> = {
    anchor: 'Anchor',
    tracker: 'Tracker',
    gateway: 'Gateway',
};

const formatDate = (d: string | null) =>
    d
        ? new Date(d).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
          })
        : '--';

const formatCost = (cents: number | null) =>
    cents != null ? `$${(cents / 100).toFixed(2)}` : '--';

const LpsShipmentDetailDialog: React.FC<Props> = ({ open, shipmentUuid, onClose }) => {
    const [shipment, setShipment] = useState<LpsShipment | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open && shipmentUuid) {
            setLoading(true);
            getShipment(shipmentUuid)
                .then((res) => {
                    const data = res.data?.data ?? res.data;
                    setShipment(data as LpsShipment);
                })
                .catch((err) => console.error('Failed to load shipment', err))
                .finally(() => setLoading(false));
        }
    }, [open, shipmentUuid]);

    const devices = shipment?.devices || [];
    const typeCounts = devices.reduce((acc, d) => {
        acc[d.device_type] = (acc[d.device_type] || 0) + 1;
        return acc;
    }, {} as Record<string, number>);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalShippingIcon color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Shipment Details
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : shipment ? (
                    <>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                                gap: 2,
                                mb: 3,
                            }}
                        >
                            <InfoItem label="Supplier" value={shipment.supplier_name} />
                            <InfoItem label="PO Number" value={shipment.po_number || '--'} />
                            <InfoItem label="Received" value={formatDate(shipment.received_at)} />
                            <InfoItem
                                label="Received By"
                                value={shipment.received_by_name || '--'}
                            />
                            <InfoItem
                                label="Total Cost"
                                value={formatCost(shipment.total_cost_cents)}
                            />
                            <InfoItem label="Device Count" value={String(devices.length)} />
                        </Box>

                        {shipment.notes && (
                            <Paper
                                variant="outlined"
                                sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: 'action.hover' }}
                            >
                                <Typography variant="caption" color="text.secondary">
                                    Notes
                                </Typography>
                                <Typography variant="body2">{shipment.notes}</Typography>
                            </Paper>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Devices
                            </Typography>
                            {Object.entries(typeCounts).map(([type, count]) => (
                                <Chip
                                    key={type}
                                    label={`${count} ${TYPE_LABELS[type as DeviceType] || type}${
                                        count > 1 ? 's' : ''
                                    }`}
                                    size="small"
                                    variant="outlined"
                                />
                            ))}
                        </Box>

                        <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
                            {/* Table header */}
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr',
                                    gap: 1,
                                    px: 2,
                                    py: 1,
                                    bgcolor: 'action.hover',
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                    Serial Number
                                </Typography>
                                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                    Type
                                </Typography>
                                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                    Model
                                </Typography>
                                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                    Status
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{ fontWeight: 600, textAlign: 'right' }}
                                >
                                    Unit Cost
                                </Typography>
                            </Box>
                            {devices.map((d, idx) => (
                                <Box
                                    key={d.uuid}
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr',
                                        gap: 1,
                                        px: 2,
                                        py: 1,
                                        borderBottom:
                                            idx < devices.length - 1 ? '1px solid' : 'none',
                                        borderColor: 'divider',
                                        '&:hover': { bgcolor: 'action.hover' },
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{ fontFamily: 'monospace', fontWeight: 500 }}
                                    >
                                        {d.serial_number}
                                    </Typography>
                                    <Typography variant="body2">
                                        {TYPE_LABELS[d.device_type]}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {d.model || '--'}
                                    </Typography>
                                    <Chip
                                        label={d.status.replace(/_/g, ' ')}
                                        size="small"
                                        color={d.status === 'in_stock' ? 'success' : 'default'}
                                        sx={{
                                            height: 22,
                                            fontSize: '0.7rem',
                                            width: 'fit-content',
                                        }}
                                    />
                                    <Typography variant="body2" sx={{ textAlign: 'right' }}>
                                        {formatCost(d.unit_cost_cents)}
                                    </Typography>
                                </Box>
                            ))}
                            {devices.length === 0 && (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ p: 2, textAlign: 'center' }}
                                >
                                    No devices in this shipment.
                                </Typography>
                            )}
                        </Paper>
                    </>
                ) : null}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

const InfoItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
            {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {value}
        </Typography>
    </Box>
);

export default LpsShipmentDetailDialog;
